import { useEffect, useState } from "react";
import { ChequeActivityData, ChequeActivityUpdate } from "../types";
import {
  useAlertStore,
  usePopupStore,
  useThemeStore,
} from "../../../../stores";
import { Column, Row } from "../../../containers";
import { Heading } from "../../../typography";
import { JustifyContent } from "../../../containers/types";
import { DateInput, TextInput } from "../../../inputs";
import { addComma } from "../../../../utils";
import { BackContainer, BankName, Timestamp, Vec2 } from "../../../../types";
import OptionList from "../../../optionlist";
import { ChequeDetails } from "../../../cheque";
import { ChequeApi } from "../../../../api";
import { LogoutIcon } from "../../../../assets/icons";
import { PopupBack, PopupClose } from "../../../../popup";

const getRenderer = <T extends keyof ChequeActivityUpdate>(
  key: T
): ((value: ChequeActivityUpdate[T]) => JSX.Element) => {
  switch (key) {
    case "account_no":
      return (value: ChequeActivityUpdate[T]) => (
        <TextInput
          label="شماره حساب"
          value={value as string}
          inputProps={{ readOnly: true }}
          key={value as string}
        />
      );
    case "bank":
      return (value: ChequeActivityUpdate[T]) => (
        <TextInput
          label="بانک"
          value={value as BankName}
          inputProps={{ readOnly: true }}
          key={value as string}
        />
      );
    case "confirmed_with":
      return (value: ChequeActivityUpdate[T]) => (
        <TextInput
          label="تایید شده به نام"
          value={value as string}
          inputProps={{ readOnly: true }}
          key={value as string}
        />
      );
    case "date":
      return (value: ChequeActivityUpdate[T]) => (
        <DateInput
          label="تاریخ"
          date={value as Timestamp}
          setDate={() => {}}
          readOnly
          key={value as string}
        />
      );
    case "name":
      return (value: ChequeActivityUpdate[T]) => (
        <TextInput
          label="نام طرف حساب"
          value={value as string}
          inputProps={{ readOnly: true }}
          key={value as string}
        />
      );
    case "number":
      return (value: ChequeActivityUpdate[T]) => (
        <TextInput
          label="شماره چک"
          value={value as string}
          inputProps={{ readOnly: true }}
          key={value as string}
        />
      );
    case "price":
      return (value: ChequeActivityUpdate[T]) => (
        <TextInput
          label="مبلغ (ریال)"
          value={addComma(value as number)}
          inputProps={{ readOnly: true }}
          key={value as string}
        />
      );
    case "shenase":
      return (value: ChequeActivityUpdate[T]) => (
        <TextInput
          label="شناسه"
          value={value as string}
          inputProps={{ readOnly: true }}
          key={value as string}
        />
      );
    default:
      return (_: ChequeActivityUpdate[T]) => <></>;
  }
};

const ChequeUpdateActivityPopup = <T extends {}>(
  props: BackContainer<ChequeActivityData, T>
) => {
  const activity = props.data as ChequeActivityUpdate;
  const theme = useThemeStore((s) => s.theme);
  const setPopupBody = usePopupStore((s) => s.setPopupBody);
  const pushAlert = useAlertStore((s) => s.pushAlert);
  const setPopupProperties = usePopupStore((s) => s.setPopupProperties);

  useEffect(() => {
    setPopupProperties({
      style: {
        maxHeight: "80%",
        overflowY: "auto",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const [fields, setFields] = useState<Array<keyof ChequeActivityUpdate>>([]);

  useEffect(() => {
    let res: Array<keyof ChequeActivityUpdate> = [];
    for (let key in activity) {
      if (activity[key as keyof ChequeActivityUpdate] && key !== "id")
        res.push(key as keyof ChequeActivityUpdate);
    }
    setFields(res);
  }, [activity]);

  const getInnerLength = (idx: number): number => {
    const x = fields.length - 3 * idx;
    return x >= 3 ? 3 : x;
  };

  const [mousePos, setMousePos] = useState<Vec2>({ x: 0, y: 0 });

  const onRightClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ): void => {
    e.preventDefault();
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  return (
    <Column
      style={{ padding: "1rem 2rem", position: "relative" }}
      gap={10}
      props={{ onContextMenu: onRightClick }}
    >
      <PopupClose />

      {props.back ? (
        <PopupBack Component={props.back.component} props={props.back.props} />
      ) : (
        <></>
      )}

      <OptionList
        mousePos={mousePos}
        options={[
          {
            title: "برو به چک",
            onClick: () => {
              ChequeApi.getById(activity.id.$oid)
                .then((c) => {
                  setPopupBody(
                    <ChequeDetails<BackContainer<ChequeActivityData, T>>
                      {...c}
                      back={{ component: ChequeUpdateActivityPopup, props }}
                    />
                  );
                })
                .catch((err) => pushAlert(err.msg || "unknown error", "error"));
            },
            Icon: LogoutIcon,
          },
        ]}
      />

      <Heading
        textStyles={{ marginBottom: "1rem" }}
        color={theme.textOnOverlay}
        size={18}
      >
        ویرایش چک
      </Heading>

      {[...Array(Math.ceil(fields.length / 3))].map((_, i) => (
        <Row
          key={i}
          style={{ width: "100%" }}
          justifyContent={JustifyContent.Evenly}
          gap={10}
          className="finance-input-row"
        >
          {[...Array(getInnerLength(i))].map((_, j) => {
            const key = fields[i * 3 + j];
            const Renderer = getRenderer(key);
            return Renderer(activity[key]);
          })}
        </Row>
      ))}
    </Column>
  );
};

export default ChequeUpdateActivityPopup;
